import * as Yup from 'yup'
import { get, camelCase, isEmpty } from 'lodash'

/**
 * Evaluates `only` conditions against form values
 * @param {Array} conditions - Array of condition objects with property, operator, value
 * @param {Object} formValues - Current form values
 * @returns {boolean} - Whether all conditions are met
 */
export const evaluateOnlyConditions = (conditions, formValues) => {
  if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
    return true
  }

  return conditions.every((condition) => {
    const { property, operator, value } = condition
    // Convert property path to camelCase segments
    const pathParts = property.split('.')
    const camelPath = pathParts.map(camelCase).join('.')
    const actualValue = get(formValues, camelPath)

    switch (operator) {
      case 'equals':
        return actualValue === value
      case 'not_equals':
        return actualValue !== value
      case 'in':
        return Array.isArray(value) && value.includes(actualValue)
      case 'not_in':
        return Array.isArray(value) && !value.includes(actualValue)
      case 'is_present':
        return !!actualValue
      default:
        return true
    }
  })
}

/**
 * Builds default values from schema
 * @param {Object} schema - Package schema
 * @returns {Object} - Default form values
 */
export const buildDefaultValues = (schema) => {
  const result = {}
  const groups = get(schema, 'groups', [])

  groups.forEach((group) => {
    const groupKey = camelCase(group.name)
    result[groupKey] = {}
    const segments = get(group, 'segments', [])

    segments.forEach((segment) => {
      const segmentKey = camelCase(segment.name)
      result[groupKey][segmentKey] = {}
      const fields = get(segment, 'fields', [])

      fields.forEach((field) => {
        const fieldName = camelCase(field.name)

        // Handle multiple/array fields
        if (field.multiple) {
          result[groupKey][segmentKey][fieldName] = field.default !== undefined
            ? (Array.isArray(field.default) ? field.default : [field.default])
            : []
          return
        }

        if (field.default !== undefined) {
          result[groupKey][segmentKey][fieldName] = field.default
        } else if (field.type === 'boolean') {
          result[groupKey][segmentKey][fieldName] = false
        } else if (field.type === 'string' || field.type === 'email' || field.type === 'domain' || field.type === 'node' || field.type === 'ingress_controller') {
          result[groupKey][segmentKey][fieldName] = ''
        } else if (field.type === 'integer' || field.type === 'number') {
          result[groupKey][segmentKey][fieldName] = 0
        } else if (field.type === 'enum') {
          result[groupKey][segmentKey][fieldName] = get(field, 'options[0]', '')
        } else {
          result[groupKey][segmentKey][fieldName] = ''
        }
      })
    })
  })

  return result
}

/**
 * Builds password policy validators
 * @param {Yup.StringSchema} validator - Base Yup string validator
 * @param {Object} policy - Password policy object
 * @param {Object} intl - react-intl object for i18n messages
 * @returns {Yup.StringSchema} - Enhanced validator with password policy rules
 */
const applyPasswordPolicy = (validator, policy, intl) => {
  if (!policy) return validator

  if (policy.minLength) {
    validator = validator.min(
      policy.minLength,
      intl.formatMessage({ id: 'passwordPolicy.minLength' }, { count: policy.minLength })
    )
  }

  if (policy.requireLowercase) {
    validator = validator.matches(
      /[a-z]/,
      intl.formatMessage({ id: 'passwordPolicy.requireLowercase' })
    )
  }

  if (policy.requireUppercase) {
    validator = validator.matches(
      /[A-Z]/,
      intl.formatMessage({ id: 'passwordPolicy.requireUppercase' })
    )
  }

  if (policy.requireNumber) {
    validator = validator.matches(
      /[0-9]/,
      intl.formatMessage({ id: 'passwordPolicy.requireNumber' })
    )
  }

  if (policy.excludeSpecial) {
    validator = validator.matches(
      /^[a-zA-Z0-9]*$/,
      intl.formatMessage({ id: 'passwordPolicy.excludeSpecial' })
    )
  }

  return validator
}

/**
 * Builds a Yup validator for a single field
 * @param {Object} field - Field definition from schema
 * @param {Object} intl - react-intl object for i18n messages
 * @returns {Yup.Schema} - Yup validator
 */
export const buildFieldValidator = (field, intl) => {
  let validator

  if (field.type === 'boolean') {
    validator = Yup.boolean()
  } else if (field.type === 'integer') {
    validator = Yup.number().integer()
  } else if (field.type === 'number') {
    validator = Yup.number()
  } else if (field.type === 'email') {
    validator = Yup.string().email(intl.formatMessage({ id: 'validation.email' }))
  } else if (field.type === 'enum' && field.options) {
    validator = Yup.string().oneOf(field.options)
  } else {
    validator = Yup.string()
  }

  // Apply password policy validation
  if (field.passwordPolicy && (field.type === 'string' || !field.type)) {
    validator = applyPasswordPolicy(validator, field.passwordPolicy, intl)
  }

  // Handle multiple/array fields
  if (field.multiple) {
    validator = Yup.array().of(validator)
  }

  // Handle required validation
  if (field.required) {
    validator = validator.required(intl.formatMessage({ id: 'validation.required' }))
  }

  return validator
}

/**
 * Builds conditional validation schema for fields with `only` conditions
 * @param {Object} field - Field definition from schema
 * @param {Object} intl - react-intl object for i18n messages
 * @param {string} fieldPath - Full dot-notation path to the field
 * @returns {Yup.Schema} - Yup validator with conditional logic
 */
const buildConditionalValidator = (field, intl, fieldPath) => {
  // Base validator that's always optional
  const baseValidator = buildFieldValidator({ ...field, required: false }, intl)

  // Get the condition to check
  const conditions = field.only

  // We need to use Yup.mixed().test() for complex conditional validation
  // because Yup's when() doesn't support deeply nested field dependencies well
  return Yup.mixed().test(
    'conditional-required',
    intl.formatMessage({ id: 'validation.required' }),
    function(value) {
      // Access the root form values from context
      const formValues = this.options.context || {}

      // Check if field should be visible
      const shouldBeVisible = evaluateOnlyConditions(conditions, formValues)

      // If not visible, always valid
      if (!shouldBeVisible) {
        return true
      }

      // If visible and required, validate
      if (field.required) {
        if (field.multiple) {
          return Array.isArray(value) && value.length > 0
        }
        return value !== undefined && value !== null && value !== ''
      }

      // If visible but not required, still validate other rules
      return baseValidator.isValidSync(value)
    }
  )
}

/**
 * Builds the complete Yup validation schema from package schema
 * @param {Object} schema - Package schema
 * @param {Object} intl - react-intl object for i18n messages
 * @returns {Yup.ObjectSchema} - Complete Yup validation schema
 */
export const buildValidationSchema = (schema, intl) => {
  const groups = get(schema, 'groups', [])
  if (groups.length === 0) return Yup.object({})

  const shape = {}

  groups.forEach((group) => {
    const groupKey = camelCase(group.name)
    const groupShape = {}
    const segments = get(group, 'segments', [])

    segments.forEach((segment) => {
      const segmentKey = camelCase(segment.name)
      const segmentShape = {}
      const fields = get(segment, 'fields', [])

      fields.forEach((field) => {
        const fieldName = camelCase(field.name)
        const fieldPath = `${groupKey}.${segmentKey}.${fieldName}`

        if (field.only && !isEmpty(field.only)) {
          // Use conditional validation for fields with `only`
          segmentShape[fieldName] = buildConditionalValidator(field, intl, fieldPath)
        } else {
          segmentShape[fieldName] = buildFieldValidator(field, intl)
        }
      })

      groupShape[segmentKey] = Yup.object(segmentShape)
    })

    shape[groupKey] = Yup.object(groupShape)
  })

  return Yup.object(shape)
}

/**
 * Collects all fields from schema into a flat array with their paths
 * @param {Object} schema - Package schema
 * @returns {Array} - Array of { field, path } objects
 */
export const collectSchemaFields = (schema) => {
  const result = []
  const groups = get(schema, 'groups', [])

  groups.forEach((group) => {
    const groupKey = camelCase(group.name)
    const segments = get(group, 'segments', [])
    segments.forEach((segment) => {
      const segmentKey = camelCase(segment.name)
      const fields = get(segment, 'fields', [])
      fields.forEach((field) => {
        const fieldName = camelCase(field.name)
        result.push({
          field,
          path: `${groupKey}.${segmentKey}.${fieldName}`
        })
      })
    })
  })

  return result
}
